import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "../shared/starfield.css";
import Navbar from "../navbar/Navbar";
import { getPublicDatasets, addDatasetToMyDatasets } from "../../services/datasets";
import { getAccessToken, hasSession } from "../../services/api.ts";
import { resolveExternalLinkUrl } from "../../utils/safeUrl";

const isAuthed = () => !!getAccessToken() || hasSession();

// Shared "Add to My <noun>s" button with three states:
//   1. unauthenticated       -> disabled + inline "please register" message
//   2. auth + preview/mock   -> disabled + "coming soon" note (no DB row to add)
//   3. auth + real, not yet  -> primary Add button (calls onAdd)
//   4. auth + real, already  -> green "Added" pill
// itemNoun is capitalized singular ("Dataset" or "Model").
function AddToMyListButton({ isMock, isAdded, adding, onAdd, itemNoun }) {
  const authed = isAuthed();
  const noun = itemNoun;
  const nounLower = noun.toLowerCase();

  if (authed && !isMock && isAdded) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "rgba(40,167,69,0.15)", border: "1px solid rgba(40,167,69,0.4)", borderRadius: "8px", color: "#28a745", fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
        <i className="fa fa-check-circle" /> Added to Your {noun}s
      </div>
    );
  }

  if (!authed) {
    return (
      <div>
        <button
          disabled
          title={`Register to save ${nounLower}s to your list`}
          style={{ padding: "10px 28px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px", cursor: "not-allowed" }}
        >
          <i className="fa fa-lock" style={{ marginRight: "8px" }} />
          Add to My {noun}s
        </button>
        <p style={{ marginTop: "10px", color: "#8a9ab5", fontFamily: "Poppins, sans-serif", fontSize: "13px", maxWidth: "520px", lineHeight: 1.5 }}>
          To keep a list of {nounLower}s you would like to use for your research, please{" "}
          <a href="/register" style={{ color: "#38b6ff", textDecoration: "underline" }}>register</a>.
        </p>
      </div>
    );
  }

  if (isMock) {
    return (
      <div>
        <button
          disabled
          title={`Preview ${nounLower} — public "add" flow coming soon`}
          style={{ padding: "10px 28px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px", cursor: "not-allowed" }}
        >
          <i className="fa fa-clock-o" style={{ marginRight: "8px" }} />
          Add to My {noun}s
        </button>
        <p style={{ marginTop: "10px", color: "#8a9ab5", fontFamily: "Poppins, sans-serif", fontSize: "13px" }}>
          Preview {nounLower} — the "add to my list" flow for external references is coming soon.
        </p>
      </div>
    );
  }

  return (
    <button
      onClick={onAdd}
      disabled={adding}
      style={{ padding: "10px 28px", background: "#38b6ff", border: "none", borderRadius: "8px", color: "#0b0f1e", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px", cursor: adding ? "not-allowed" : "pointer", opacity: adding ? 0.7 : 1 }}
    >
      <i className="fa fa-plus" style={{ marginRight: "8px" }} />
      {adding ? "Adding..." : `Add to My ${noun}s`}
    </button>
  );
}

const MOCK_DATASETS = [
  { id: "nsclc-radiomics", name: "NSCLC-Radiomics", researcher: "Researcher0", institution: "Institution0", samples: 422, modalities: ["CT"], diseases: ["Cancer"], anatomies: ["Lung, Head and Neck"], source: "https://www.cancerimagingarchive.net/collection/nsclc-radiomics/", iconEmoji: "🫁" },
  { id: "kits", name: "KiTS", researcher: "Researcher4", institution: "Medical Institute Y", samples: 589, modalities: ["CT"], diseases: ["Cancer"], anatomies: ["Kidney"], source: "https://kits-challenge.org/kits23/", iconEmoji: "🫘" },
  { id: "medical-segmentation-decathlon", name: "Medical Segmentation Decathlon", researcher: "Researcher1", institution: "InstitutionX", samples: 1569, modalities: ["CT", "MRI"], diseases: ["Cancer"], anatomies: ["Liver, Brain, Hippocampus, Lung, Prostate, Cardiac, Pancreas, Colon, Spleen"], source: "http://medicaldecathlon.com/", iconEmoji: "🧍‍♀️" },
  { id: "cq500", name: "CQ500", researcher: "ResearcherX", institution: "InstitutionY", samples: 51, modalities: ["CT"], diseases: ["Intracranial hemorrhages"], anatomies: ["Brain"], source: "https://www.zenodo.org/records/8063221", iconEmoji: "🧠" },
  { id: "sinocT", name: "sinoCT", researcher: "ResearcherZ", institution: "InstitutionZ", samples: 9000, modalities: ["CT"], diseases: ["Normal vs Abnormal"], anatomies: ["Brain"], source: "https://aimi.stanford.edu/datasets/sinoct", iconEmoji: "🧠" },
  { id: "3d-cta-images", name: "3D CTA Images", researcher: "ResearcherA", institution: "Cardio Institute", samples: 1000, modalities: ["CT"], diseases: ["Coronary artery disease"], anatomies: ["Heart"], source: "https://github.com/XiaoweiXu/ImageCAS-A-Large-Scale-Dataset-and-Benchmark-for-Coronary-Artery-Segmentation-based-on-CT", iconEmoji: "❤️" },
  { id: "ribfrac-challenge", name: "RibFrac Challenge", researcher: "ResearcherB", institution: "Trauma Research Center", samples: 660, modalities: ["CT"], diseases: ["Rib fractures"], anatomies: ["Ribs"], source: "https://ribfrac-challenge.grand-challenge.org/", iconEmoji: "🦴" },
  { id: "organ-ac-smnist", name: "Organ{A,C,S}MNIST", researcher: "ResearcherC", institution: "BioMed Lab", samples: 718067, modalities: ["CT", "MRI"], diseases: ["Various"], anatomies: ["Multiple"], source: "https://www.nature.com/articles/s41597-022-01721-8", iconEmoji: "🧍‍♂️" },
  { id: "intracranial-hemorrhage-detection", name: "Intracranial Hemorrhage Detection", researcher: "ResearcherD", institution: "Neuro Imaging Center", samples: 874000, modalities: ["CT"], diseases: ["Intracranial hemorrhage"], anatomies: ["Brain"], source: "https://www.kaggle.com/c/rsna-intracranial-hemorrhage-detection/", iconEmoji: "🧠" },
  { id: "rad-chest", name: "RAD-CHEST", researcher: "ResearcherF", institution: "Chest Imaging Institute", samples: 35747, modalities: ["X-ray"], diseases: ["Various"], anatomies: ["Chest"], source: "https://www.zenodo.org/records/6406114", iconEmoji: "🫁" },
  { id: "ct-rate", name: "CT-RATE", researcher: "ResearcherG", institution: "Radiology Data Lab", samples: 50188, modalities: ["CT"], diseases: ["Multiple"], anatomies: ["Chest"], source: "https://huggingface.co/datasets/ibrahimhamamci/CT-RATE", iconEmoji: "🫁" },
  { id: "m3d", name: "M3D", researcher: "ResearcherH", institution: "Multi-Modal Imaging Lab", samples: 120092, modalities: ["CT", "MRI"], diseases: ["Various"], anatomies: ["Multiple"], source: "https://github.com/BAAI-DCAI/M3D?tab=readme-ov-file#data", iconEmoji: "🧍‍♀️" },
];

const TAG_COLORS = {
  CT: "#2563eb", MRI: "#7c3aed", "X-ray": "#0891b2", PET: "#b45309",
};

function ModalityTag({ label }) {
  const bg = TAG_COLORS[label] || "#374151";
  return (
    <span style={{ background: bg, color: "#fff", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontFamily: "Poppins, sans-serif", fontWeight: 600, marginRight: "4px" }}>
      {label}
    </span>
  );
}

function DatasetList({ datasets, selected, onSelect, query, onQueryChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Search */}
      <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ position: "relative" }}>
          <i className="fa fa-search" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#5a6a80", fontSize: "14px" }} />
          <input
            type="text"
            placeholder="Search datasets..."
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            style={{
              width: "100%", padding: "10px 12px 10px 36px", background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff",
              fontFamily: "Poppins, sans-serif", fontSize: "14px", outline: "none", boxSizing: "border-box",
            }}
          />
          {query && (
            <button onClick={() => onQueryChange("")} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#5a6a80", cursor: "pointer", padding: 0 }}>
              <i className="fa fa-times" />
            </button>
          )}
        </div>
        <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a6a80", fontFamily: "Poppins, sans-serif" }}>
          {datasets.length} dataset{datasets.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* List — explicit maxHeight so it scrolls independently */}
      <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 260px)" }}>
        {datasets.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "15px" }}>
            No datasets match your search
          </div>
        )}
        {datasets.map(ds => {
          const isActive = selected?.id === ds.id;
          return (
            <button
              key={ds.id}
              onClick={() => onSelect(ds)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "14px",
                padding: "14px 16px", border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)",
                background: isActive ? "rgba(56,182,255,0.12)" : "transparent",
                borderLeft: isActive ? "3px solid #38b6ff" : "3px solid transparent",
                cursor: "pointer", textAlign: "left", transition: "background 0.15s",
              }}
            >
              <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", flexShrink: 0 }}>
                {ds.iconEmoji || <i className="fa fa-database" style={{ color: "#38b6ff" }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: isActive ? "#38b6ff" : "#e2e8f0", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {ds.name}
                </div>
                <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "12px", marginTop: "3px" }}>
                  {(ds.modalities || []).join(", ")} · {(ds.anatomies || []).join(", ").substring(0, 30)}{(ds.anatomies || []).join(", ").length > 30 ? "…" : ""}
                </div>
              </div>
              <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "12px", flexShrink: 0, textAlign: "right" }}>
                <div style={{ color: "#a0aec0", fontWeight: 600 }}>{(ds.samples || ds.patient_count || 0).toLocaleString()}</div>
                <div style={{ fontSize: "11px" }}>samples</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ExternalDetailPanel({ dataset }) {
  if (!dataset) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#5a6a80", fontFamily: "Poppins, sans-serif" }}>
      Select a dataset to view details
    </div>
  );

  return (
    <div style={{ padding: "32px 36px", overflowY: "auto", height: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "28px" }}>
        <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "rgba(255,255,255,0.08)", border: "2px solid rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "34px", flexShrink: 0 }}>
          {dataset.iconEmoji}
        </div>
        <div>
          <h2 style={{ margin: 0, color: "#ffffff", fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "20px" }}>{dataset.name}</h2>
          <p style={{ margin: "4px 0 0", color: "#8a9ab5", fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>{dataset.researcher}</p>
          <p style={{ margin: "2px 0 0", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "13px" }}>{dataset.institution}</p>
        </div>
      </div>

      <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: "24px" }} />

      <h4 style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "15px", marginBottom: "20px" }}>Dataset Information</h4>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 24px", marginBottom: "24px" }}>
        <div>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}># Samples</div>
          <div style={{ color: "#ffffff", fontFamily: "Poppins, sans-serif", fontSize: "20px", fontWeight: 700 }}>{dataset.samples.toLocaleString()}</div>
        </div>
        <div>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Modalities</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>{dataset.modalities.map(m => <ModalityTag key={m} label={m} />)}</div>
        </div>
        <div>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Anatomies</div>
          <div style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "15px" }}>{dataset.anatomies.join(", ")}</div>
        </div>
        <div>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Diseases</div>
          <div style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "15px" }}>{dataset.diseases.join(", ")}</div>
        </div>
      </div>

      <div style={{ marginBottom: "28px" }}>
        <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Source</div>
        <a href={resolveExternalLinkUrl(dataset.source) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: "#38b6ff", fontFamily: "Poppins, sans-serif", fontSize: "13px", wordBreak: "break-all" }}>
          {dataset.source}
        </a>
      </div>

      <AddToMyListButton isMock={true} isAdded={false} adding={false} onAdd={() => {}} itemNoun="Dataset" />
    </div>
  );
}

function SharedDetailPanel({ dataset, onAdd, adding }) {
  if (!dataset) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#5a6a80", fontFamily: "Poppins, sans-serif" }}>
      Select a dataset to view details
    </div>
  );

  return (
    <div style={{ padding: "32px 36px", overflowY: "auto", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "28px" }}>
        <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "rgba(23,162,184,0.15)", border: "2px solid rgba(23,162,184,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "30px", flexShrink: 0 }}>
          <i className="fa fa-database" style={{ color: "#17a2b8" }} />
        </div>
        <div>
          <h2 style={{ margin: 0, color: "#ffffff", fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "22px" }}>{dataset.name}</h2>
          <p style={{ margin: "4px 0 0", color: "#8a9ab5", fontFamily: "Poppins, sans-serif", fontSize: "13px" }}>
            by {dataset.created_by_user?.first_name || dataset.created_by_user?.username || "Unknown"}
          </p>
          <p style={{ margin: "2px 0 0", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "12px" }}>Community Shared Dataset</p>
        </div>
      </div>

      <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: "24px" }} />

      <h4 style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px", marginBottom: "20px" }}>Dataset Information</h4>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px", marginBottom: "24px" }}>
        {[["Patients", dataset.patient_count || 0], ["Images", dataset.image_count || 0], ["Documents", dataset.document_count || 0], ["Created", dataset.created_at ? new Date(dataset.created_at).toLocaleDateString() : "N/A"]].map(([label, val]) => (
          <div key={label}>
            <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>{label}</div>
            <div style={{ color: "#ffffff", fontFamily: "Poppins, sans-serif", fontSize: "18px", fontWeight: 600 }}>{val}</div>
          </div>
        ))}
      </div>

      {dataset.description && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Description</div>
          <p style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "14px", lineHeight: 1.6, margin: 0 }}>{dataset.description}</p>
        </div>
      )}

      <AddToMyListButton isMock={false} isAdded={!!dataset.is_added} adding={adding} onAdd={onAdd} itemNoun="Dataset" />
    </div>
  );
}

export default function OpenDataset() {
  const [activeTab, setActiveTab] = useState("external");
  const [datasets] = useState(MOCK_DATASETS);
  const [query, setQuery] = useState("");
  const [selectedDataset, setSelectedDataset] = useState(MOCK_DATASETS[0]);

  const [sharedDatasets, setSharedDatasets] = useState([]);
  const [sharedQuery, setSharedQuery] = useState("");
  const [selectedShared, setSelectedShared] = useState(null);
  const [loadingShared, setLoadingShared] = useState(true);
  const [addingDataset, setAddingDataset] = useState(false);

  useEffect(() => {
    getPublicDatasets()
      .then(data => { setSharedDatasets(data); if (data.length) setSelectedShared(data[0]); })
      .catch(() => {})
      .finally(() => setLoadingShared(false));
  }, []);

  const filteredExternal = query.trim()
    ? datasets.filter(d => [d.name, d.researcher, d.institution, ...d.modalities, ...d.anatomies].join(" ").toLowerCase().includes(query.toLowerCase()))
    : datasets;

  const filteredShared = sharedQuery.trim()
    ? sharedDatasets.filter(d => [d.name, d.description || "", d.created_by_user?.username || ""].join(" ").toLowerCase().includes(sharedQuery.toLowerCase()))
    : sharedDatasets;

  const handleAddToMyDatasets = async () => {
    if (!selectedShared) return;
    setAddingDataset(true);
    try {
      const result = await addDatasetToMyDatasets(selectedShared.id);
      toast.success(result.message || `"${selectedShared.name}" added to your datasets!`);
      const data = await getPublicDatasets();
      setSharedDatasets(data);
      setSelectedShared(data.find(d => d.id === selectedShared.id) || selectedShared);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add dataset");
    } finally {
      setAddingDataset(false);
    }
  };

  const panelStyle = {
    background: "rgba(15,17,32,0.85)",
    borderRadius: "14px",
    border: "1px solid rgba(255,255,255,0.08)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div className="dashboard-page" style={{ minHeight: "100vh", color: "#fff", display: "flex", flexDirection: "column" }}>
      <div className="starfield-background" />
      <Navbar />

      <main style={{ flex: 1, display: "flex", flexDirection: "column", padding: "20px 28px 28px", position: "relative", zIndex: 1, minHeight: 0 }}>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "4px", marginBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: "0" }}>
          {[["external", "External Datasets"], ["shared", `Community Shared (${sharedDatasets.length})`]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
                fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px",
                color: activeTab === key ? "#38b6ff" : "#5a6a80",
                borderBottom: activeTab === key ? "2px solid #38b6ff" : "2px solid transparent",
                transition: "all 0.15s", marginBottom: "-1px",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Title */}
        <h2 style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "18px", color: "#e2e8f0", marginBottom: "16px" }}>
          {activeTab === "external" ? "Open Medical Dataset Hub" : "Community Shared Datasets"}
        </h2>

        {/* Two-column layout */}
        <div style={{ display: "flex", gap: "16px", flex: 1, minHeight: 0, height: "calc(100vh - 240px)" }}>

          {/* Left — list */}
          <div style={{ ...panelStyle, flex: "0 0 420px" }}>
            {activeTab === "external" && (
              <DatasetList datasets={filteredExternal} selected={selectedDataset} onSelect={setSelectedDataset} query={query} onQueryChange={setQuery} />
            )}
            {activeTab === "shared" && (
              loadingShared
                ? <div style={{ padding: "40px", textAlign: "center", color: "#5a6a80", fontFamily: "Poppins, sans-serif" }}>Loading...</div>
                : <DatasetList datasets={filteredShared} selected={selectedShared} onSelect={setSelectedShared} query={sharedQuery} onQueryChange={setSharedQuery} />
            )}
          </div>

          {/* Right — detail */}
          <div style={{ ...panelStyle, flex: 1 }}>
            {activeTab === "external" && <ExternalDetailPanel dataset={selectedDataset} />}
            {activeTab === "shared" && <SharedDetailPanel dataset={selectedShared} onAdd={handleAddToMyDatasets} adding={addingDataset} />}
          </div>
        </div>
      </main>
    </div>
  );
}
